import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { Alert, Button, Col, Form, Input, Radio, Row, Space, Spin } from "antd";
import { observer } from "mobx-react-lite";

import { ApiError } from "../api/constants";
import {
  createWebgis,
  getServerLocations,
  getWebgisRefs,
  pollWebgisStatus,
} from "../api/webgis";
import { WebGisConnectBlock } from "../components/webgis/WebGisConnectBlock";
import { WebGisCreatingModal } from "../components/webgis/WebGisCreatingModal";
import i18n from "../i18n";
import { userStore } from "../stores/userStore";
import { usePageTitle } from "../utils/usePageTitle";

import "./WebGisCreatePage.css";

import type { ApiFieldError, ServerLocation } from "../api/webgis";

const DOMAIN_RE = /^[a-z0-9][a-z0-9-]{2,60}[a-z0-9]$/;

interface DomainInputProps {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  domain: string;
}

function DomainInput({ value = "", onChange, domain }: DomainInputProps) {
  return (
    <Space.Compact style={{ width: "100%" }}>
      <Input
        size="large"
        value={value}
        onChange={onChange}
        placeholder="youraddress"
        className="webgis-create-page__subdomain-input"
      />
      {domain && <Input size="large" value={`.${domain}`} readOnly />}
    </Space.Compact>
  );
}

export const WebGisCreatePage = observer(function WebGisCreatePage() {
  const { t } = useTranslation();
  usePageTitle(t("webgis.create"));
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [domain, setDomain] = useState("");
  const [locations, setLocations] = useState<ServerLocation[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pollingRef = useRef(false);
  const submittingRef = useRef(false);

  const { webgis, webgisLoaded } = userStore;

  useEffect(() => {
    if (
      !webgisLoaded ||
      !webgis ||
      webgis.state_transition !== "wait_for_create"
    )
      return;
    if (pollingRef.current) return;
    pollingRef.current = true;
    setShowModal(true);
    const controller = new AbortController();
    pollWebgisStatus("started", undefined, undefined, controller.signal)
      .then((data) => {
        navigate("/webgis?webgis-status=started", { replace: true });
        userStore.setWebgis(data);
      })
      .catch((err) => {
        if (err.name !== "AbortError") throw err;
      });
    return () => {
      controller.abort();
      pollingRef.current = false;
    };
  }, [webgisLoaded, webgis, navigate]);

  useEffect(() => {
    if (!webgisLoaded) return;
    Promise.all([getWebgisRefs(), getServerLocations()])
      .then(([refs, locs]) => {
        setDomain(refs.domain);
        setLocations(locs);
        if (userStore.isRuRegion) {
          const moscowLocation = locs.find(
            (loc) => loc.display_name === "🇷🇺 Moscow",
          );
          if (moscowLocation) {
            form.setFieldValue("location_id", moscowLocation.id);
          }
        }
      })
      .catch(() => setLoadError(true));
  }, [webgisLoaded]);

  if (webgisLoaded && webgis && webgis.state_transition !== "wait_for_create") {
    return (
      <Navigate
        to={showModal ? "/webgis?webgis-status=started" : "/webgis"}
        replace
      />
    );
  }

  // Maps known API error detail strings to translation keys
  const translateApiError = (detail: string): string => {
    if (detail === "This name is already taken")
      return t("webgis.subdomainTaken");
    return detail;
  };

  const handleSubmit = async (values: {
    subdomain_name: string;
    location_id: number;
  }) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      await createWebgis({
        subdomain_name: values.subdomain_name.toLowerCase(),
        language: i18n.language,
        location_id: values.location_id,
      });
      setShowModal(true);
      const webgisData = await pollWebgisStatus("started");
      navigate("/webgis?webgis-status=started", { replace: true });
      userStore.setWebgis(webgisData);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { errors?: ApiFieldError[] };
        if (body.errors?.length) {
          form.setFields(
            body.errors.map((e) => ({
              name: e.source.pointer,
              errors: [translateApiError(e.detail)],
            })),
          );
        }
      }
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="page webgis-create-page">
      <h1>{t("webgis.create")}</h1>
      {!webgisLoaded ? (
        <div className="webgis-create-page__center">
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={48}>
          <Col xs={24} lg={14}>
            <p
              className="webgis-create-page__description"
              dangerouslySetInnerHTML={{ __html: t("webgis.description") }}
            />

            {loadError ? (
              <Alert
                type="error"
                description={t("webgis.errorLoad")}
                showIcon
              />
            ) : locations.length === 0 ? (
              <div className="webgis-create-page__center">
                <Spin />
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className="webgis-create-page__form"
              >
                <Form.Item
                  name="subdomain_name"
                  label={t("webgis.chooseDomain")}
                  rules={[
                    { required: true, message: t("webgis.domainRequired") },
                    {
                      validator: (_, value) =>
                        !value || DOMAIN_RE.test(value)
                          ? Promise.resolve()
                          : Promise.reject(t("webgis.domainInvalid")),
                    },
                  ]}
                >
                  <DomainInput domain={domain} />
                </Form.Item>

                <Form.Item
                  name="location_id"
                  label={t("webgis.location")}
                  extra={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("webgis.locationHint"),
                      }}
                    />
                  }
                  rules={[
                    { required: true, message: t("webgis.locationRequired") },
                  ]}
                >
                  <Radio.Group
                    className="webgis-create-page__locations"
                    size="large"
                  >
                    {locations.map((loc) => (
                      <Radio.Button key={loc.id} value={loc.id}>
                        {loc.display_name}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>

                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={submitting}
                >
                  {t("webgis.create")}
                </Button>
              </Form>
            )}
          </Col>

          <Col xs={0} lg={10}>
            <WebGisConnectBlock />
          </Col>
        </Row>
      )}

      <WebGisCreatingModal open={showModal} />
    </div>
  );
});
